import { motion, useReducedMotion } from 'motion/react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useI18nContext } from '@/i18n/i18n-react';
import { CAMPAIGN_SCHEDULER_INTERVAL_MINUTES } from '@/lib/campaign-schedule';
import { mobileLayer } from '@/lib/mobile-layer';

const EASE = [0.16, 1, 0.3, 1] as const;

/** Korza-blue circle and check drawn stroke by stroke, with a soft halo. */
function DrawnCheck({ reduceMotion }: { reduceMotion: boolean }) {
  const draw = (delay: number, duration: number) =>
    reduceMotion
      ? { initial: false as const }
      : {
          initial: { pathLength: 0 },
          animate: { pathLength: 1 },
          transition: { delay, duration, ease: EASE },
        };
  return (
    <div className="relative size-16 text-primary" aria-hidden>
      {!reduceMotion && (
        <motion.span
          className="absolute inset-0 rounded-full bg-primary/20"
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: [0.6, 1.6], opacity: [0, 0.8, 0] }}
          transition={{ delay: 0.45, duration: 0.9, ease: EASE }}
        />
      )}
      <svg viewBox="0 0 64 64" className="relative size-16">
        <motion.circle
          cx="32"
          cy="32"
          r="29"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          {...draw(0, 0.5)}
        />
        <motion.path
          d="M20 33.5 28.5 42 44.5 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          {...draw(0.4, 0.35)}
        />
      </svg>
    </div>
  );
}

/**
 * Confirmation after creating a campaign (PUSH-05 AC 6): "Campanha agendada"
 * with when it goes out, or, for "Agora", "Campanha na fila · Sai em até
 * {n} minuto(s)" with n = the prod scheduler cadence. Arrives as the
 * composer's main button turns into a check: the check draws itself, the
 * texts and actions follow.
 */
export function ComposerSuccess({
  kind,
  whenLabel,
  campaignId,
  tenantSlug,
  onCreateAnother,
}: {
  kind: 'scheduled' | 'sentNow';
  whenLabel: string;
  campaignId: string;
  tenantSlug: string;
  onCreateAnother(): void;
}) {
  const { LL } = useI18nContext();
  const reduceMotion = useReducedMotion() ?? false;
  const rise = (delay: number) =>
    reduceMotion
      ? { initial: false as const }
      : {
          initial: { opacity: 0, y: 12 },
          animate: { opacity: 1, y: 0 },
          transition: { delay, duration: 0.5, ease: EASE },
        };

  return (
    <div
      role="status"
      className="mx-auto flex max-w-md flex-col items-center gap-4 py-12 text-center"
    >
      <DrawnCheck reduceMotion={reduceMotion} />
      <motion.div className="space-y-1" {...rise(0.35)}>
        <h2 className="text-xl font-semibold">
          {kind === 'sentNow'
            ? LL.campaignComposer.success.queuedTitle()
            : LL.campaignComposer.success.scheduledTitle()}
        </h2>
        <p className="text-muted-foreground">
          {kind === 'sentNow'
            ? LL.campaignComposer.success.queuedWhen({
                minutes: CAMPAIGN_SCHEDULER_INTERVAL_MINUTES,
              })
            : whenLabel}
        </p>
      </motion.div>
      <motion.div
        className="flex w-full flex-col gap-2 sm:flex-row sm:justify-center"
        {...rise(0.5)}
      >
        <Button asChild className={mobileLayer.touch}>
          <Link to={`/${tenantSlug}/campaigns/${campaignId}`}>
            {LL.campaignComposer.success.viewCampaign()}
          </Link>
        </Button>
        <Button
          type="button"
          variant="outline"
          className={mobileLayer.touch}
          onClick={onCreateAnother}
        >
          {LL.campaignComposer.success.createAnother()}
        </Button>
      </motion.div>
    </div>
  );
}
